import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { ClassificationModal } from '../components/ClassificationModal';
import { repositoryClassificationsApi } from '../classificationsApi';
import type { RepositoryClassificationListItem } from '../repositoryTypes';

/**
 * The information classification catalog — Repository → Settings → Classifications.
 *
 * **A catalog belongs in a Settings menu, never in the rail** — the test is whether a reader
 * goes there to do work or to change what the work draws on. That is why the breadcrumb links
 * back through Settings: it is the only way here, so the crumb is the way out.
 */
export function ClassificationsIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<RepositoryClassificationListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    classification: RepositoryClassificationListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchClassifications = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      repositoryClassificationsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (classification: RepositoryClassificationListItem): MenuItem[] => [
      {
        key: `edit-${classification.id}`,
        label: 'Edit',
        permission: 'repository.update',
        onClick: () => setEditing(classification),
      },
      {
        key: `restore-${classification.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'repository.restore',
        disabledReason: classification.isActive ? 'This classification is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', classification }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await repositoryClassificationsApi.deactivate(confirming.classification.id);
      } else {
        await repositoryClassificationsApi.activate(confirming.classification.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<RepositoryClassificationListItem>
        module="repository"
        // Reached through Repository → Settings, so Settings links: that crumb is the way back.
        breadcrumb={[
          'Document Management',
          { label: 'Repository', to: '/repository' },
          { label: 'Settings', to: '/repository/settings' },
          'Classifications',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['RepositoryClassification']}
        rowKey={(classification) => classification.id}
        fetch={fetchClassifications}
        reloadKey={reloadKey}
        searchPlaceholder="Search classifications"
        emptyMessage="No classifications yet. Add the ones this organization recognises — a document cannot be registered without one."
        onCreate={() => setCreating(true)}
        createPermission="repository.create"
        // The anchor opens the profile; Edit is in the row menu. The read key, not the write
        // one — opening a record is not changing it.
        onRowOpen={(classification) =>
          navigate(`/repository/settings/classifications/${classification.id}`)
        }
        openPermission="repository.read"
        rowActions={rowActions}
        onDelete={(classification) => setConfirming({ kind: 'deactivate', classification })}
        deletePermission="repository.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(classification) =>
          classification.isActive ? null : 'This classification is already deleted.'
        }
        onClearData={() => repositoryClassificationsApi.clear()}
        clearDataPermission="repository.clear-data"
        otherInfo={(classification) => classification}
      />

      {creating || editing !== null ? (
        <ClassificationModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete classification' : 'Restore classification'}
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring puts something back.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`repository-classification-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.classification.name}" stops being offered when a document is registered.`
                  : `"${confirming.classification.name}" is offered again when a document is registered.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.classification.documentCount > 0 ? (
                <p>
                  {confirming.classification.documentCount} document
                  {confirming.classification.documentCount === 1 ? '' : 's'} carr
                  {confirming.classification.documentCount === 1 ? 'ies' : 'y'} it. They keep
                  resolving its name — how a document was classified does not become wrong
                  because the vocabulary was tidied — but it cannot be chosen for anything new.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted classification can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<RepositoryClassificationListItem>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 240,
    flex: true,
    sortable: true,
    render: (classification) => <TextCell value={classification.name} />,
  },
  {
    key: 'description',
    header: 'Definition',
    width: 420,
    summary: true,
    render: (classification) => <TextCell value={classification.description} />,
  },
  {
    key: 'isConfidential',
    header: 'Confidential',
    width: 130,
    summary: true,
    /*
      A rule, not a label — it decides whether a document under this classification is refused
      into a folder shared beyond those explicitly granted it. Rendered as words rather than a
      tick so the column reads the same in the list view, which has no cell to put a tick in.
    */
    render: (classification) => (
      <TextCell value={classification.isConfidential ? 'Confidential' : 'Not confidential'} />
    ),
  },
  {
    key: 'documentCount',
    header: 'Documents',
    width: 110,
    align: 'right',
    sortable: true,
    summary: true,
    render: (classification) => (
      <TextCell value={String(classification.documentCount)} mono numeric />
    ),
  },
  {
    key: 'sortOrder',
    header: 'Order',
    width: 90,
    align: 'right',
    sortable: true,
    /*
      The STORED ordering value, under a heading that says so. The matrix reads least to most
      restricted and the order is gapped by tens, so this is not a position counted from one —
      a page of records cannot know the whole ladder.
    */
    render: (classification) => <TextCell value={String(classification.sortOrder)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (classification) =>
      classification.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
